/**
 * React Hooks for Tasks
 *
 * Provides custom hooks for managing task state and operations
 * using React Query for caching and synchronization. All client-side calls
 * go through directApiClient — server actions were retired in LF-3017.
 */

'use client';

import {
  useQuery,
  useMutation,
  useQueryClient,
  UseQueryOptions,
  UseMutationOptions
} from '@tanstack/react-query';
import { toast } from '@/lib/toast';
import { FRESH_DATA_CONFIG } from '../query-config';
import { directApiClient } from '@/lib/api/direct-client';
import type { Task, CreateTaskInput, UpdateTaskInput } from './types';
import { taskKeys, matterKeys } from '@/lib/query-keys';

const DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;

class TaskValidationError extends Error {
  validationErrors: Array<{ field: string; message: string }>;
  constructor(
    message: string,
    validationErrors: Array<{ field: string; message: string }>
  ) {
    super(message);
    this.name = 'TaskValidationError';
    this.validationErrors = validationErrors;
  }
}

// ============================================================================
// Cache Key Utilities
// ============================================================================

/**
 * @deprecated Use taskKeys from '@/lib/query-keys' instead
 */
export const TASK_CACHE_KEYS = taskKeys;

// ============================================================================
// Query Hooks
// ============================================================================

/**
 * Hook to fetch tasks scheduled for today or past
 * Uses directApiClient for direct API calls (no server action overhead)
 *
 * @param lawFirmUserId - Filter tasks by assigned user
 * @param tags - Filter tasks by matter tags
 * @param q - Search query
 */
export function useTodayTasks(
  lawFirmUserId?: number,
  tags?: string[],
  q?: string,
  options?: Omit<UseQueryOptions<Task[], Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: TASK_CACHE_KEYS.today(lawFirmUserId, tags, q),
    queryFn: async () => {
      // Build query params
      const params: Record<string, string | string[]> = {};
      if (lawFirmUserId) params.lawFirmUserId = String(lawFirmUserId);
      if (tags && tags.length > 0) params.tags = tags.join(',');
      if (q) params.q = q;

      return directApiClient.get<Task[]>('/tasks/today', { params });
    },
    ...FRESH_DATA_CONFIG,
    ...options
  });
}

/**
 * Hook to fetch tasks scheduled for future dates
 * Uses directApiClient for direct API calls (no server action overhead)
 *
 * @param lawFirmUserId - Filter tasks by assigned user
 * @param tags - Filter tasks by matter tags
 * @param q - Search query
 */
export function useUpcomingTasks(
  lawFirmUserId?: number,
  tags?: string[],
  q?: string,
  options?: Omit<UseQueryOptions<Task[], Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: TASK_CACHE_KEYS.upcoming(lawFirmUserId, tags, q),
    queryFn: async () => {
      // Build query params
      const params: Record<string, string | string[]> = {};
      if (lawFirmUserId) params.lawFirmUserId = String(lawFirmUserId);
      if (tags && tags.length > 0) params.tags = tags.join(',');
      if (q) params.q = q;

      return directApiClient.get<Task[]>('/tasks/upcoming', { params });
    },
    ...FRESH_DATA_CONFIG,
    ...options
  });
}

// ============================================================================
// Mutation Hooks
// ============================================================================

// NOTE: These mutations operate on the **global** task list (today / upcoming /
// scheduling) without a matter context, so they bypass `getDependencies` and
// invalidate the `taskKeys.all` and `matterKeys.all` buckets directly. This is
// the documented exception to the LF-3021 single-source rule — see
// `lib/query-keys/CLAUDE.md` for context. Per-matter task mutations (CTA
// completions, stage actions) invalidate `taskKeys`/`matterKeys` buckets
// directly at the call site (e.g. `cta-handlers-integrated.ts`).

/**
 * Hook to create a new task
 */
export function useCreateTask(
  options?: UseMutationOptions<Task, Error, CreateTaskInput>
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateTaskInput) => {
      if (!input.matterId) {
        throw new TaskValidationError('Matter ID is required', [
          { field: 'matterId', message: 'Matter ID is required' }
        ]);
      }
      if (!input.scheduledDate) {
        throw new TaskValidationError('Scheduled date is required', [
          { field: 'scheduledDate', message: 'Scheduled date is required' }
        ]);
      }
      if (!DATE_REGEX.test(input.scheduledDate)) {
        throw new TaskValidationError('Invalid date format. Use YYYY-MM-DD', [
          { field: 'scheduledDate', message: 'Must be in YYYY-MM-DD format' }
        ]);
      }

      return directApiClient.post<Task>('/tasks', input);
    },
    onSuccess: (_task) => {
      // Invalidate all task queries to refetch updated data
      queryClient.invalidateQueries({ queryKey: TASK_CACHE_KEYS.all });
      // Invalidate matters cache since scheduling affects which view matters appear in
      queryClient.invalidateQueries({ queryKey: matterKeys.all });

      // No toast - instant UI feedback is sufficient
    },
    onError: (error: Error) => {
      console.error('Create task error:', error);
      toast.error(error.message || 'Failed to schedule matter');
    },
    ...options
  });
}

/**
 * Hook to update an existing task
 */
export function useUpdateTask(
  options?: UseMutationOptions<
    Task,
    Error,
    { taskId: number; updates: UpdateTaskInput },
    { previousTasks: { today: unknown; upcoming: unknown } }
  >
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      taskId,
      updates
    }: {
      taskId: number;
      updates: UpdateTaskInput;
    }) => {
      // Whitelist allowed fields to prevent stray properties hitting the API
      const allowedUpdates: UpdateTaskInput = {};
      if (updates.scheduledDate !== undefined)
        allowedUpdates.scheduledDate = updates.scheduledDate;
      if (updates.position !== undefined)
        allowedUpdates.position = updates.position;
      if (updates.status !== undefined) allowedUpdates.status = updates.status;
      if (updates.description !== undefined)
        allowedUpdates.description = updates.description;

      if (
        allowedUpdates.scheduledDate &&
        !DATE_REGEX.test(allowedUpdates.scheduledDate)
      ) {
        throw new TaskValidationError('Invalid date format. Use YYYY-MM-DD', [
          { field: 'scheduledDate', message: 'Must be in YYYY-MM-DD format' }
        ]);
      }

      return directApiClient.put<Task>(`/tasks/${taskId}`, allowedUpdates);
    },
    onMutate: async ({ taskId, updates }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: TASK_CACHE_KEYS.all });

      // Snapshot previous value
      const previousTasks = {
        today: queryClient.getQueryData(TASK_CACHE_KEYS.today()),
        upcoming: queryClient.getQueryData(TASK_CACHE_KEYS.upcoming())
      };

      // Optimistically update task lists
      queryClient.setQueriesData<Task[]>(
        { queryKey: TASK_CACHE_KEYS.all },
        (old) => {
          if (!Array.isArray(old)) return old;

          // Update the task with new values
          const updated = old.map((task) =>
            task.id === taskId ? { ...task, ...updates } : task
          );

          // If position was updated, re-sort by position
          if ('position' in updates) {
            return updated.sort((a, b) => {
              const posA = a.position ?? 999;
              const posB = b.position ?? 999;
              return posA - posB;
            });
          }

          return updated;
        }
      );

      return { previousTasks };
    },
    onSuccess: (data, variables) => {
      // Check if this is a position-only update (drag and drop reordering)
      const isPositionOnlyUpdate =
        Object.keys(variables.updates).length === 1 &&
        'position' in variables.updates;

      // Check if this is a date-only update (schedule change)
      const isDateOnlyUpdate =
        Object.keys(variables.updates).length === 1 &&
        'scheduledDate' in variables.updates;

      if (isPositionOnlyUpdate) {
        // For position updates, don't invalidate - rely on local state in DraggableMatterList
        // The backend update happens in background, next natural refetch will sync
      } else if (isDateOnlyUpdate) {
        // For date updates, invalidate to move tasks between lists (today/upcoming)
        // This is necessary so tasks appear in the correct view immediately
        queryClient.invalidateQueries({ queryKey: TASK_CACHE_KEYS.all });
        queryClient.invalidateQueries({ queryKey: matterKeys.all });
        // No toast - instant UI feedback is sufficient
      } else {
        // For other updates, invalidate to refetch
        queryClient.invalidateQueries({ queryKey: TASK_CACHE_KEYS.all });
        queryClient.invalidateQueries({ queryKey: matterKeys.all });
        // No toast - instant UI feedback is sufficient
      }
    },
    onError: (error: Error, variables, context) => {
      console.error('Update task error:', error);

      // Rollback optimistic updates
      if (context?.previousTasks) {
        queryClient.setQueryData(
          TASK_CACHE_KEYS.today(),
          context.previousTasks.today
        );
        queryClient.setQueryData(
          TASK_CACHE_KEYS.upcoming(),
          context.previousTasks.upcoming
        );
      }

      toast.error(error.message || 'Failed to update matter');
    },
    ...options
  });
}

/**
 * Hook to delete a task
 */
export function useDeleteTask(
  options?: UseMutationOptions<
    void,
    Error,
    number,
    { previousTasks: { today: unknown; upcoming: unknown } }
  >
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (taskId: number) => {
      await directApiClient.delete(`/tasks/${taskId}`);
    },
    onMutate: async (taskId: number) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: TASK_CACHE_KEYS.all });

      // Snapshot previous value
      const previousTasks = {
        today: queryClient.getQueryData(TASK_CACHE_KEYS.today()),
        upcoming: queryClient.getQueryData(TASK_CACHE_KEYS.upcoming())
      };

      // Optimistically remove task from lists
      queryClient.setQueriesData<Task[]>(
        { queryKey: TASK_CACHE_KEYS.all },
        (old) => {
          if (!Array.isArray(old)) return old;
          return old.filter((task) => task.id !== taskId);
        }
      );

      return { previousTasks };
    },
    onSuccess: () => {
      // Invalidate queries to refetch
      queryClient.invalidateQueries({ queryKey: TASK_CACHE_KEYS.all });
      // Invalidate matters cache since clearing schedule affects which view matters appear in
      queryClient.invalidateQueries({ queryKey: matterKeys.all });

      // Don't show toast here - let the caller show it to avoid duplicates
      // when deleting multiple tasks for the same matter
    },
    onError: (error: Error, taskId, context) => {
      console.error('Delete task error:', error);

      // Rollback optimistic updates
      if (context?.previousTasks) {
        queryClient.setQueryData(
          TASK_CACHE_KEYS.today(),
          context.previousTasks.today
        );
        queryClient.setQueryData(
          TASK_CACHE_KEYS.upcoming(),
          context.previousTasks.upcoming
        );
      }

      toast.error(error.message || 'Failed to clear schedule');
    },
    ...options
  });
}
